/**
 * Single source of truth for the `save_questionnaire_answers` A/B test.
 *
 * Weight is controlled by the `save_questionnaire_answers_ab_test` variable
 * in the variables table. The variant is first assigned in Questionnaire.tsx
 * (after the server response provides the weight) and persisted in
 * localStorage by ABTestManager. Subsequent reads (e.g. PageTracker) just
 * load the already-assigned variant.
 */

import { ABTestManager } from "./ABTestManager";

export type SaveAnswerVariant = "A" | "B";

export const SAVE_ANSWER_TEST_NAME = "save_questionnaire_answers";
export const SAVE_ANSWER_VARIANTS: readonly SaveAnswerVariant[] = ["A", "B"];

/**
 * Assign the variant using the server-provided weight for Group B.
 * Call this once from Questionnaire.tsx after the server response.
 * ABTestManager persists the assignment — subsequent calls with the same
 * testName just return the stored value regardless of the weight.
 */
export const assignSaveAnswerVariant = (weightB: number): SaveAnswerVariant => {
  const manager = new ABTestManager({
    testName: SAVE_ANSWER_TEST_NAME,
    variants: [...SAVE_ANSWER_VARIANTS],
    weights: [1 - weightB, weightB],
  });
  const variant = manager.getVariant();
  return variant === "B" ? "B" : "A";
};

/**
 * Read the already-assigned variant from localStorage. Use this from places
 * that don't have access to the server-provided weight (e.g. PageTracker).
 * If no variant was assigned yet (user never hit the questionnaire page),
 * falls back to "A" (control / no save-restore behaviour).
 */
export const getSaveAnswerVariant = (): SaveAnswerVariant => {
  if (typeof window === "undefined") return "A";
  const stored = localStorage.getItem(`abtest_${SAVE_ANSWER_TEST_NAME}`);
  if (stored === "B") return "B";
  return "A";
};
